import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { X } from "@phosphor-icons/react";
import type { ButtonProps } from "@mui/material/Button";

import TargetActionCard from "./TargetActionCard";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TargetAction = "withGoal" | "move";

export interface Target {
  id: string;
  name: string;
}

export interface Goal {
  id: string;
  name: string;
}

export interface TargetActionState {
  targetId: string;
  action: TargetAction;
  /** Defined only when action is "move". */
  destinationGoalId: string;
}

interface TargetState {
  action: TargetAction;
  destinationGoalId: string;
}

export interface GoalDispositionPanelProps {
  /**
   * Controls the title, radio label, confirm button label and color:
   * - `"archive"` → "Archive Goal" / "Archive with this goal" / primary
   * - `"delete"` → "Delete Goal" / "Delete with this goal" / error
   */
  variant: "archive" | "delete";
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback fired when the user dismisses the dialog. */
  onClose: () => void;
  /** Name of the goal being archived or deleted, shown in the description. */
  goalName: string;
  /** Active targets that must be resolved before the goal can be disposed. */
  targets: Target[];
  /**
   * Goals available as migration destinations in the "Move to another goal"
   * select. The caller should exclude the goal being disposed from this list.
   */
  availableGoals: Goal[];
  /**
   * Called with the resolved action for every target when the user confirms.
   * The caller is responsible for the mutation and passing `isLoading`.
   * For the `"archive"` and `"delete"` variants, a non-empty `rationale`
   * string is always passed.
   */
  onSubmit: (actions: TargetActionState[], rationale: string) => void;
  /** Shows a spinner on the confirm button and disables both action buttons. */
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Variant configuration
// ---------------------------------------------------------------------------

interface VariantConfig {
  title: string;
  primaryActionLabel: string;
  confirmLabel: string;
  confirmColor: ButtonProps["color"];
}

const VARIANT_CONFIG: Record<"archive" | "delete", VariantConfig> = {
  archive: {
    title: "Archive Goal",
    primaryActionLabel: "Archive with this goal",
    confirmLabel: "Archive Goal",
    confirmColor: "primary",
  },
  delete: {
    title: "Delete Goal",
    primaryActionLabel: "Delete with this goal",
    confirmLabel: "Delete Goal",
    confirmColor: "error",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Builds an object mapping target IDs to their initial action and destination goal ID.
 * @param targets - The targets to build the initial states for.
 * @returns An object mapping target IDs to their initial action and destination goal ID.
 */
const buildInitialStates = (targets: Target[]): Record<string, TargetState> =>
  Object.fromEntries(
    targets.map((t) => [t.id, { action: "withGoal" as TargetAction, destinationGoalId: "" }])
  );

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Generic dialog for archiving or deleting a goal that has active targets.
 * For each target the user decides whether to dispose of it alongside the goal
 * or move it to a different goal. Submitting is blocked until every "move"
 * target has a destination goal selected.
 *
 * Prefer the thin wrappers `ArchiveGoalPanel` and `DeleteGoalPanel` over
 * consuming this component directly.
 *
 * @param props
 * @param props.variant        - Drives title, labels and confirm button color
 * @param props.open           - Controls dialog visibility
 * @param props.onClose        - Called when the user dismisses the dialog
 * @param props.goalName       - Name of the goal being archived/deleted
 * @param props.targets        - Active targets to resolve
 * @param props.availableGoals - Goals available as migration destinations
 * @param props.onSubmit       - Called with resolved target actions on confirm
 * @param props.isLoading      - Disables actions and shows spinner while mutating
 */
const GoalDispositionPanel: React.FC<GoalDispositionPanelProps> = ({
  variant,
  open,
  onClose,
  goalName,
  targets,
  availableGoals,
  onSubmit,
  isLoading = false,
}) => {
  const config = VARIANT_CONFIG[variant];

  const [targetStates, setTargetStates] = useState<Record<string, TargetState>>(() =>
    buildInitialStates(targets)
  );
  const [rationale, setRationale] = useState("");

  // Reset per-target choices and rationale every time the dialog opens.
  useEffect(() => {
    if (open) {
      setTargetStates(buildInitialStates(targets));
      setRationale("");
    }
  }, [open, targets]);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  /** True when every "move" target has a destination goal chosen. */
  const isSubmitReady = targets.every((t) => {
    const state = targetStates[t.id];
    return state?.action === "withGoal" || Boolean(state?.destinationGoalId);
  });

  const isRationaleRequired = variant === "archive" || variant === "delete";

  const isDisabled = isLoading || !isSubmitReady || (isRationaleRequired && !rationale.trim());

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleTargetChange = (
    targetId: string,
    update: { action?: TargetAction; destinationGoalId?: string }
  ) => {
    setTargetStates((prev) => ({
      ...prev,
      [targetId]: { ...prev[targetId], ...update },
    }));
  };

  const handleSubmit = () => {
    const actions: TargetActionState[] = targets.map((t) => ({
      targetId: t.id,
      action: targetStates[t.id]?.action ?? "withGoal",
      destinationGoalId: targetStates[t.id]?.destinationGoalId ?? "",
    }));
    onSubmit(actions, isRationaleRequired ? rationale : "");
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const targetCount = targets.length;
  // Use variant-scoped IDs so each dialog has unique, predictable aria attributes.
  const dialogId = `${variant}-goal-dialog`;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      aria-labelledby={`${dialogId}-title`}
      aria-describedby={`${dialogId}-description`}
      maxWidth="sm"
      fullWidth
    >
      {/* Title row */}
      <DialogTitle
        id={`${dialogId}-title`}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 5,
          px: 7,
        }}
      >
        <Typography component="span" variant="h4" fontWeight={500}>
          {config.title}
        </Typography>
        <IconButton
          aria-label={`Close ${config.title.toLowerCase()} dialog`}
          onClick={onClose}
          disabled={isLoading}
          size="small"
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ px: 3, py: 0, pb: 3 }}>
        <Stack component="section" direction="column" gap={6}>
          {/* Description */}
          <Typography id={`${dialogId}-description`} variant="body1" color="text.secondary">
            &ldquo;{goalName}&rdquo; has {targetCount} active{" "}
            {targetCount === 1 ? "target" : "targets"}. Choose what to do with each one.
          </Typography>

          {/* Rationale – only shown for the archive or delete variant */}
          {isRationaleRequired && (
            <Stack direction="column" gap={2}>
              <Typography variant="subtitle2">What is your rationale for this action?</Typography>
              <TextField
                multiline
                rows={5}
                fullWidth
                placeholder="Enter your rationale..."
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                inputProps={{ "aria-label": `Rationale for ${variant} goal` }}
              />
              <Typography variant="caption">
                This will be included in future reassessment treatment plans.
              </Typography>
            </Stack>
          )}

          {/* Target cards */}
          {targets.map((target) => (
            <TargetActionCard
              key={target.id}
              target={target}
              action={targetStates[target.id]?.action ?? "withGoal"}
              destinationGoalId={targetStates[target.id]?.destinationGoalId ?? ""}
              availableGoals={availableGoals}
              primaryActionLabel={config.primaryActionLabel}
              onChange={(update) => handleTargetChange(target.id, update)}
            />
          ))}
        </Stack>
      </DialogContent>

      {/* Footer actions */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="secondary"
          size="medium"
          onClick={onClose}
          disabled={isLoading}
          aria-label={`Cancel and close ${config.title.toLowerCase()} dialog`}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color={config.confirmColor}
          size="medium"
          onClick={handleSubmit}
          disabled={isDisabled}
          loading={isLoading}
          aria-label={`Confirm ${config.title.toLowerCase()}`}
        >
          {config.confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default GoalDispositionPanel;
